import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import PageHeader, { ToolbarCaptionButton } from '@/components/PageHeader';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import ProfessionPicker from '@/components/ProfessionPicker';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { processStory } from '@/services/storyService';
import { listStoryTypes } from '@/services/storyRepo';
import {
  STORY_WRITE_LOADING_MESSAGES,
  STORY_WRITE_LOADING_INTERVAL_MS,
  STORY_WRITE_LOADING_HINT,
} from '@/lib/storyFormLoadingMessages';

const PATHS = [
  {
    id: 'free_flow',
    label: 'Write freely',
    hint: 'One continuous piece, in your own words.',
  },
  {
    id: 'structured',
    label: 'Use the five-part format',
    // Hint is hydrated from the selected story type's section labels at render.
  },
];

// Used only when the types query fails or returns nothing — keeps submit usable.
const FALLBACK_TYPE = {
  id: 'personal',
  display_name: 'Transformation story',
  section_1_label: 'Before',
  section_2_label: 'Struggle',
  section_3_label: 'Turning point',
  section_4_label: 'What helped',
  section_5_label: 'Outcome',
  section_count: 5,
};

const sectionLabelsOf = (t) => [
  t.section_1_label, t.section_2_label, t.section_3_label,
  t.section_4_label, t.section_5_label,
].slice(0, t.section_count || 5);

const SubmitStoryPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [path, setPath] = useState('free_flow');
  const [title, setTitle] = useState('');
  const [rawText, setRawText] = useState('');
  const [sections, setSections] = useState({
    section_1: '', section_2: '', section_3: '', section_4: '', section_5: '',
  });
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [professions, setProfessions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitMessageIdx, setSubmitMessageIdx] = useState(0);

  // Wait for DB-backed types before painting the form so section labels and
  // the type selector do not flash from fallback → real data.
  const [storyTypes, setStoryTypes] = useState(null);
  const [storyTypeId, setStoryTypeId] = useState('personal');

  useEffect(() => {
    let cancelled = false;
    listStoryTypes()
      .then((types) => {
        if (cancelled) return;
        const list = types?.length ? types : [FALLBACK_TYPE];
        setStoryTypes(list);
        setStoryTypeId((cur) => {
          if (list.some((t) => t.id === cur)) return cur;
          return list.find((t) => t.id === 'personal')?.id || list[0].id;
        });
      })
      .catch((err) => {
        console.error('Failed to load story types:', err);
        if (cancelled) return;
        setStoryTypes([FALLBACK_TYPE]);
        setStoryTypeId(FALLBACK_TYPE.id);
      });
    return () => { cancelled = true; };
  }, []);

  const selectedType = useMemo(
    () => (storyTypes?.find((t) => t.id === storyTypeId) || FALLBACK_TYPE),
    [storyTypes, storyTypeId],
  );
  const sectionLabels = useMemo(() => sectionLabelsOf(selectedType), [selectedType]);
  const structuredHint = useMemo(() => sectionLabels.join(' · '), [sectionLabels]);

  // Use user?.id (primitive) so this only fires on actual sign-in/out,
  // not on every Supabase token refresh / tab focus event.
  const userId = user?.id;
  const typesReady = storyTypes != null;
  const showPageSpinner = authLoading || !typesReady;

  useEffect(() => {
    if (!authLoading && !userId) {
      toast.error('Please sign in to share a story');
      navigate('/login');
    }
  }, [userId, authLoading, navigate]);

  useEffect(() => {
    if (!submitting) {
      setSubmitMessageIdx(0);
      return undefined;
    }
    const timer = setInterval(() => {
      setSubmitMessageIdx((prev) => (prev + 1) % STORY_WRITE_LOADING_MESSAGES.length);
    }, STORY_WRITE_LOADING_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [submitting]);

  const updateSection = (key, value) => {
    setSections((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (path === 'structured') {
      const anyFilled = Object.values(sections).some((s) => s && s.trim());
      if (!anyFilled) {
        toast.error('Fill at least one section before submitting');
        return;
      }
      if (!title.trim()) {
        toast.error('A short title helps — please add one');
        return;
      }
    } else {
      if (!rawText.trim() || rawText.trim().length < 50) {
        toast.error('Write at least a few sentences (50+ characters)');
        return;
      }
    }

    setSubmitting(true);
    try {
      const payload = {
        story_type: storyTypeId,
        path,
        is_anonymous: isAnonymous,
        author_display_name: isAnonymous ? null : user?.name,
        professions,
      };
      if (path === 'structured') {
        payload.sections = sections;
        payload.title = title.trim();
      } else {
        payload.raw_text = rawText.trim();
        if (title.trim()) payload.title = title.trim();
      }
      const result = await processStory(payload);
      toast.success('Your story is live.');
      // Send the author to the review screen so they can curate AI-picked
      // dimensions + tune the highlight. They can skip from there if they want.
      if (result?.story_id) {
        navigate(`/story/${result.story_id}/review?from=submit`);
      } else {
        setTimeout(() => navigate('/'), 1500);
      }
    } catch (error) {
      console.error('Error submitting story:', error);
      toast.error(error.message || 'Failed to submit story. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <PageHeader>
        <ToolbarCaptionButton
          label="Back to Home"
          shortLabel="Home"
          onClick={() => navigate('/')}
          data-testid="back-home-btn"
        />
      </PageHeader>

      {showPageSpinner ? (
        <div
          className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-24 flex justify-center"
          data-testid="submit-story-types-loading"
        >
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#C05621]" />
            <p className="mt-4 text-[#6B665E]">Loading…</p>
          </div>
        </div>
      ) : (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-serif tracking-tight leading-tight text-[#2D2A26] mb-4">
            Share Your Story
          </h2>
          <p className="text-base md:text-lg leading-relaxed text-[#6B665E]">
            Pick how you’d like to write. Your story appears as soon as you submit; moderators may act on reports if needed.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8" data-testid="story-submission-form">
          {storyTypes.length > 1 && (
            <div className="bg-white rounded-xl border border-[#E6E2D6] p-6 space-y-4" data-testid="story-type-block">
              <h3 className="text-2xl font-serif font-medium text-[#2D2A26]">What kind of story?</h3>
              <p className="text-sm text-[#6B665E]">Each shape has its own five sections.</p>
              <div
                className="grid grid-cols-1 md:grid-cols-2 gap-3"
                data-testid="story-type-selector"
              >
                {storyTypes.map((t) => {
                  const selected = storyTypeId === t.id;
                  return (
                    <label
                      key={t.id}
                      className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                        selected
                          ? 'border-[#C05621] bg-[#FBF4EB]'
                          : 'border-[#E6E2D6] hover:border-[#C05621]/40'
                      }`}
                    >
                      <input
                        type="radio"
                        name="story_type"
                        value={t.id}
                        checked={selected}
                        onChange={() => setStoryTypeId(t.id)}
                        className="mt-1 accent-[#C05621]"
                        data-testid={`story-type-option-${t.id}`}
                      />
                      <div className="min-w-0">
                        <div className="font-medium text-[#2D2A26]">{t.display_name}</div>
                        <div className="text-sm text-[#6B665E] mt-1">
                          {sectionLabelsOf(t).join(' · ')}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl border border-[#E6E2D6] p-6 space-y-4">
            <h3 className="text-2xl font-serif font-medium text-[#2D2A26]">How would you like to write?</h3>
            <div className="space-y-3" data-testid="path-selector">
              {PATHS.map((opt) => {
                const hint = opt.id === 'structured' ? structuredHint : opt.hint;
                return (
                  <label
                    key={opt.id}
                    className={`flex items-start space-x-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                      path === opt.id
                        ? 'border-[#C05621] bg-[#FBF4EB]'
                        : 'border-[#E6E2D6] hover:border-[#C05621]/40'
                    }`}
                  >
                    <input
                      type="radio"
                      name="path"
                      value={opt.id}
                      checked={path === opt.id}
                      onChange={() => setPath(opt.id)}
                      className="mt-1 accent-[#C05621]"
                      data-testid={`path-option-${opt.id}`}
                    />
                    <div>
                      <div className="font-medium text-[#2D2A26]">{opt.label}</div>
                      <div className="text-sm text-[#6B665E] mt-1">{hint}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-[#E6E2D6] p-6 space-y-6">
            <h3 className="text-2xl font-serif font-medium text-[#2D2A26]">Basic Information</h3>

            <div>
              <Label htmlFor="title">Story Title{path === 'structured' ? ' *' : ''}</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={
                  path === 'structured'
                    ? 'Give your story a title'
                    : 'Optional — we may suggest one from your text'
                }
                required={path === 'structured'}
                data-testid="story-title-input"
                className="mt-2"
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="anonymous"
                checked={isAnonymous}
                onCheckedChange={(checked) => setIsAnonymous(!!checked)}
                data-testid="anonymous-toggle"
              />
              <Label htmlFor="anonymous" className="cursor-pointer">
                Share anonymously
              </Label>
            </div>

            <ProfessionPicker value={professions} onChange={setProfessions} />
          </div>

          {path !== 'structured' && (
            <div className="bg-white rounded-xl border border-[#E6E2D6] p-6 space-y-4" data-testid="freeflow-block">
              <h3 className="text-2xl font-serif font-medium text-[#2D2A26]">Your Story</h3>
              <Textarea
                value={rawText}
                onChange={(e) => setRawText(e.target.value)}
                placeholder="Start with where you were before, then what shifted, then where you are now…"
                required
                data-testid="story-rawtext-input"
                className="min-h-64"
              />
            </div>
          )}

          {path === 'structured' && (
            <div className="bg-white rounded-xl border border-[#E6E2D6] p-6 space-y-6" data-testid="structured-block">
              <h3 className="text-2xl font-serif font-medium text-[#2D2A26]">Your Story Structure</h3>
              {sectionLabels.map((label, idx) => {
                const key = `section_${idx + 1}`;
                return (
                  <div key={key}>
                    <Label htmlFor={key}>{label}</Label>
                    <Textarea
                      id={key}
                      value={sections[key]}
                      onChange={(e) => updateSection(key, e.target.value)}
                      placeholder={`Describe: ${label.toLowerCase()}…`}
                      data-testid={`story-${key}-input`}
                      className="mt-2 min-h-32"
                    />
                  </div>
                );
              })}
            </div>
          )}

          <Button
            type="submit"
            disabled={submitting}
            className="w-full rounded-full px-8 py-6 text-lg font-medium bg-[#C05621] hover:bg-[#A0461A] text-white shadow-lg hover:shadow-xl"
            data-testid="submit-story-btn"
          >
            {submitting ? STORY_WRITE_LOADING_MESSAGES[submitMessageIdx] : 'Publish story'}
          </Button>
          {submitting && (
            <p className="text-sm text-[#6B665E] text-center -mt-4" data-testid="submit-loading-hint">
              {STORY_WRITE_LOADING_HINT}
            </p>
          )}
        </form>
      </div>
      )}
    </div>
  );
};

export default SubmitStoryPage;
